import {
  Modal,
  Box,
  FormControl,
  FormLabel,
  Divider,
  Button,
  CircularProgress,
  Chip,
  Typography,
  Select,
  MenuItem,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import { LabelStyle } from "../../Common/styles/form";
import {
  ModalDrawerStyles,
  ModalHeader,
  ModalDrawerButtonStyles,
} from "../../Common/styles/modal";
import { InputWrapper } from "../ParticipantDetails/Medication/style";
import { DrawerForm } from "../../Common/styles/drawer";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { useParams } from "react-router-dom";
import http from "../../../utils/http";
import { AxiosResponse } from "axios";

type Props = {
  showModal: boolean;
  closeModal: () => void;
  refreshPage: () => void;
  existingTags: any[];
};

const AddTagModal = ({
  showModal,
  closeModal,
  refreshPage,
  existingTags,
}: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [tagsList, setTagsList] = useState<any[]>([]);
  const [activeTagsList, setActiveTagsList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { id: studyId, participantId } = useParams();

  useEffect(() => {
    if (existingTags && studyId) {
      const tags = existingTags
        .filter((tag: any) => tag.studyId === studyId)
        .map((tag: any) => ({
          id: tag.id,
          name: tag.name,
        }));
      setSelectedTags(tags);
    }
  }, [existingTags, studyId]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(`/platform-tags`);
        const newData = res?.data?.data?.map((item: any) => ({
          id: item?.id,
          name: item?.name,
          status: item?.status,
          category: item?.category,
        }));

        const assignedInStudy = new Set(
          existingTags
            ?.filter((tag: any) => tag.studyId === studyId)
            .map((tag: any) => tag.id) || []
        );

        setActiveTagsList(
          newData.filter(
            (tag: any) =>
              tag.status === "active" &&
              tag.category === "user" &&
              !assignedInStudy.has(tag.id)
          )
        );
        setTagsList(newData);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchTags();
  }, [studyId, existingTags]);

  const handleSubmit = async () => {
    try {
      setSubmitLoader(true);

      const existingTagIds =
        existingTags
          ?.filter((tag: any) => tag.studyId === studyId)
          ?.map((tag: any) => tag.id) || [];

      const selectedTagIds = selectedTags.map((tag: any) => tag.id);

      const addIds = selectedTagIds.filter(
        (tagId: string) => !existingTagIds.includes(tagId)
      );
      const removeIds = existingTagIds.filter(
        (tagId: string) => !selectedTagIds.includes(tagId)
      );

      const body = {
        studyId: studyId,
        participantId: participantId,
        addIds,
        removeIds,
      };

      const res = await http.post(`/participant-tags`, body);
      toastMessage("success", res.data.message);
      closeModal();
      refreshPage();
    } catch (error) {
      errorToastMessage(error as Error);
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title={"Add Tag"} onCloseClick={closeModal} />
        <Box sx={DrawerForm}>
          <Box>
            <Box>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="tags">
                  Select Tags
                </FormLabel>
                <Select
                  multiple
                  value={selectedTags.map((tag: any) => tag.id)}
                  onChange={(e) => {
                    const selectedTagIds = e.target.value as string[];
                    const selectedTagsWithNames = selectedTagIds.map((id) => {
                      const tag =
                        tagsList.find((t) => t.id === id) ||
                        selectedTags.find((t) => t.id === id);
                      return { id, name: tag?.name || id };
                    });
                    setSelectedTags(selectedTagsWithNames);
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : activeTagsList?.length > 0 ? (
                    activeTagsList?.map((tag: any) => (
                      <MenuItem
                        disabled={loading}
                        key={tag?.id}
                        value={tag?.id}
                      >
                        {tag?.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No tags found</MenuItem>
                  )}
                </Select>
              </FormControl>
              {selectedTags?.length > 0 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTags.length} tag
                    {selectedTags.length !== 1 ? "s" : ""} selected
                  </Typography>
                </Box>
              )}
              {selectedTags?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mr: 2,
                    mb: 3,
                    flexWrap: "wrap",
                    mt: 2,
                  }}
                >
                  {selectedTags?.map((tag: any) => (
                    <Chip
                      sx={{
                        backgroundColor: "#F6F1F8",
                        paddingX: "10px",
                        paddingY: "2px",
                        borderRadius: "6px",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "primary.main",
                        m: 0.5,
                      }}
                      deleteIcon={
                        <CloseRounded
                          sx={{
                            color: "text.primary",
                            height: "16px",
                            width: "16px",
                          }}
                        />
                      }
                      key={tag.id}
                      label={tag.name}
                      onDelete={() => {
                        const updatedTags = selectedTags?.filter(
                          (t: any) => t.id !== tag.id
                        );
                        setSelectedTags(updatedTags);
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: "auto" }}>
            <Divider />
            <Box sx={ModalDrawerButtonStyles}>
              {!submitLoader ? (
                <>
                  <Button onClick={closeModal} variant="outlined">
                    Cancel
                  </Button>
                  <Button onClick={handleSubmit} variant="contained">
                    Save
                  </Button>
                </>
              ) : (
                <CircularProgress size={25} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddTagModal;
